import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import SvgIcon from "../../../../../parts/SvgIcon";
import AddOptionDropdown from "../../../programs/components/AddOptionDropdown";
import { calculateNewOptionMealPlannerData } from "../../../../../utils/meal-planner-helper";

const MealPlannerTabs = ({ mealPlan, locals, tabState, setTabState, planMode }) => {
  const { t } = useTranslation("athlete-portal");
  const { query } = locals || {};
  const { viewMode, plan: nutritionPlanId } = query || {};
  const isTemplate =
    (mealPlan?.type === "template" || !nutritionPlanId) &&
    mealPlan?.type !== "library";
  const [isAddOptionOpened, setIsAddOptionOpened] = useState(false);
  const availableOptions = Array.from({ length: 5 }, (_, i) => {
    const count = i + 1;
    return {
      text: t("mealPlannerTabs.addOption_other", { count }),
      value: count,
    };
  });
  availableOptions[0].text = t("mealPlannerTabs.addOption_one", { count: 1 });
  /**
   * Handle the add multiple options
   */
  const handleAddOptions = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      const eventType = e.type;

      if (eventType === "mouseenter") {
        setIsAddOptionOpened(true);
      } else if (eventType === "mouseleave") {
        setIsAddOptionOpened(false);
      } else if (eventType === "click") {
        setIsAddOptionOpened((prevState) => !prevState);
      }
    }
  };

  /**
   * Handle adding options to the meal plan
   */
  const handleAddOptionsClick = (newOptions = 1) => {
    const optionLabel =
      newOptions === 1
        ? t("mealPlannerTabs.newOption")
        : t("mealPlannerTabs.newOptions", { count: newOptions });
    const message = t("mealPlannerTabs.addOptionsConfirm", { label: optionLabel });

    if (!window.confirm(message)) {
      return;
    }

    // Get mealPlanId from mealPlan or locals.query
    const mealPlanIdToUse = mealPlan?._id || locals?.query?.mealPlan;
    if (!mealPlanIdToUse) {
      console.error("mealPlanId is required");
      return;
    }

    // Calculate meals and title(s) based on the last option of the same type
    const result = calculateNewOptionMealPlannerData(
      tabState,
      mealPlan,
      newOptions
    );

    const url = `/actions/meal-planner/handle-simplified-meal-plan-option?mealPlanId=${mealPlanIdToUse}`;
    
    // If generating a single option, use 'option' (singular)
    // If generating multiple options, use 'options' (plural)
    const body =
      newOptions === 1
        ? {
            handle: "add",
            dayType: tabState,
            option: {
              title: result.title,
              meals: result.meals,
            },
          }
        : {
            handle: "add",
            dayType: tabState,
            options: result,
          };

    submit(url, {
      method: "POST",
      body,
    });
  };

  // For simplified meal plan templates, hide the tabs since only trainingDayOptions is used
  // The template will always show trainingDayOptions[0], and restDayOptions[0] remains empty
  const shouldHideTabs = isTemplate && planMode === "simplified";

  return (
    <nav role="tablist">
      {!shouldHideTabs && (
      <div className="tabs-container">
        <a
          id="training-tab"
          onClick={() => setTabState("training")}
          aria-current={tabState === "training" ? true : undefined}
        >
          {t("mealPlannerTabs.trainingDay")}
        </a>
        <a
          id="rest-tab"
          onClick={() => setTabState("rest")}
          aria-current={tabState === "rest" ? true : undefined}
        >
          {t("mealPlannerTabs.restDay")}
        </a>
      </div>
      )}
      {!viewMode && !(isTemplate && planMode === "simplified") && (
      <hgroup>
        <div
          className="add-option-dropdown-wrapper"
          onMouseLeave={handleAddOptions}
        >
          <button
            data-button-type="primary"
            onClick={(e) => {
              e.preventDefault();
              handleAddOptionsClick();
            }}
          >
            <span>{t("mealPlannerTabs.addMultipleOption")}</span>
            <SvgIcon
              src={
                isAddOptionOpened
                  ? "/img/svg/angle-up.svg"
                  : "/img/svg/angle-down.svg"
              }
              noFilterOnLight={true}
              onClickFn={handleAddOptions}
              onMouseEnterFn={handleAddOptions}
            />
          </button>
          {isAddOptionOpened && (
            <AddOptionDropdown
              options={availableOptions}
              handleAddOptionClick={(e, value) => {
                e.preventDefault();
                handleAddOptionsClick(value);
              }}
            />
          )}
        </div>
      </hgroup>
      )}
    </nav>
  );
};

export default MealPlannerTabs;

MealPlannerTabs.propTypes = {
  locals: PropTypes.object,
  mealPlan: PropTypes.object,
  tabState: PropTypes.string,
  setTabState: PropTypes.func,
  planMode: PropTypes.string,
};
